import React, { useMemo } from "react";
import { ChevronDown } from "lucide-react";

const PURPLE_GRADIENT = "linear-gradient(45deg, #9E85DD 25%, #7B39EC 59.86%)";

const BIKE_TYPE_IMG = {
  "Scooter":     "/assets/collection/bikes/scooter.png",
  "Commuter":    "/assets/collection/bikes/commuter.png",
  "Cruiser":     "/assets/collection/bikes/cruiser.png",
  "Sports Bike": "/assets/collection/bikes/sportsbike.png",
  "Adventure":   "/assets/collection/bikes/adventure.png",
  "Electric":    "/assets/collection/bikes/electric-bike.png",
};

const BikeFilterSidebar = ({
  originalData,
  selectedFilters,
  setSelectedFilters,
  sortOption,
  setSortOption,
  yearFrom,
  setYearFrom,
  yearTo,
  setYearTo,
}) => {
  const years = useMemo(() => {
    const ys = [...new Set((originalData || []).map(c => c.modelYear).filter(Boolean))].sort();
    return ys;
  }, [originalData]);

  const brands = useMemo(() => {
    return [...new Set((originalData || []).map(c => c.brand || c.make).filter(Boolean))].sort();
  }, [originalData]);

  const vehicleTypes = useMemo(() => {
    return [...new Set((originalData || []).map(c => c.bodyTypeText).filter(Boolean))].sort();
  }, [originalData]);

  const seatingOptions = useMemo(() => {
    return [...new Set((originalData || []).map(c => c.seater).filter(Boolean))].sort((a, b) => Number(a) - Number(b));
  }, [originalData]);

  const transmissionTypes = useMemo(() => {
    return [...new Set((originalData || []).map(c => c.transmissionTypeText).filter(Boolean))].sort();
  }, [originalData]);

  const fuelTypes = useMemo(() => {
    return [...new Set((originalData || []).map(c => c.fuelTypeText).filter(Boolean))].sort();
  }, [originalData]);

  const toggle = (key, value) => {
    setSelectedFilters(prev => {
      const current = prev[key] || [];
      const updated = current.includes(value) ? current.filter(v => v !== value) : [...current, value];
      const next = { ...prev, [key]: updated };
      if (updated.length === 0) delete next[key];
      return next;
    });
  };

  const clearAll = () => {
    setSelectedFilters({});
    setSortOption("lowtohigh");
    setYearFrom("");
    setYearTo("");
  };

  return (
    <div className="w-full h-full bg-white rounded-[24px] flex flex-col font-sans overflow-hidden">
      {/* Header */}
      <div className="w-full px-4 py-3 bg-white flex justify-between items-center shrink-0 shadow-[0_4px_6px_-6px_rgba(0,0,0,0.1)]">
        <div className="flex items-center gap-2">
          <img src="/assets/icons/ev/sliders.svg" alt="" className="w-4 h-4" />
          <span className="text-[#374151] text-[20px] font-medium font-['Outfit']">Filter</span>
        </div>
        <button
          type="button"
          onClick={clearAll}
          className="text-[#6b7280] text-[12px] font-medium underline hover:text-[#374151] transition-colors bg-transparent border-none cursor-pointer"
        >
          Clear All
        </button>
      </div>

      <div className="flex-1 overflow-y-auto overflow-x-hidden flex flex-col gap-4 px-3 py-3" style={{ scrollbarWidth: "thin", scrollbarColor: "#babdee #f3f4f6" }}>

        {/* Sort */}
        <div className="flex items-center gap-2">
          <span className="text-[14px] font-medium text-black whitespace-nowrap font-['Outfit']">Sort by :</span>
          <div className="flex-1 bg-white border border-[#e5e7eb] rounded-[8px] flex items-center justify-between px-3 py-1.5">
            <span className="text-[14px] font-medium text-black font-['Outfit']">
              {sortOption === "lowtohigh" ? "Low to high" : "High to low"}
            </span>
            <button
              type="button"
              onClick={() => setSortOption(s => s === "lowtohigh" ? "hightolow" : "lowtohigh")}
              className="w-8 h-8 rounded-full flex items-center justify-center shrink-0 shadow-[1px_-1px_2px_0px_rgba(43,115,255,0.3),-1px_1px_2px_0px_rgba(31,83,197,0.5)]"
              style={{ background: PURPLE_GRADIENT }}
            >
              <img src="/assets/icons/ev/sort-icon.svg" alt="" className="w-[15px] h-[15px] invert" />
            </button>
          </div>
        </div>

        <div className="w-full h-px bg-neutral-200 shrink-0" />

        {/* Model Year */}
        {years.length > 0 && (
          <>
            <div className="flex flex-col gap-3">
              <span className="text-[#1a1917] text-[16px] font-semibold font-['Outfit'] tracking-[0.6px]">Model Year</span>
              <div className="flex gap-3">
                <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                  <span className="text-[#777] text-[10px] font-medium font-['Outfit']">From</span>
                  <div className="relative bg-white border border-[#e6e6e6] rounded-[6px] h-[40px] flex items-center justify-between pl-3 pr-2">
                    <select
                      value={yearFrom}
                      onChange={e => setYearFrom(e.target.value)}
                      className="bg-transparent outline-none appearance-none text-[#212121] text-[12px] font-medium font-['Outfit'] cursor-pointer w-full"
                    >
                      <option value="">Any</option>
                      {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <ChevronDown size={14} className="text-[#374151] shrink-0 pointer-events-none absolute right-2" />
                  </div>
                </div>
                <div className="flex flex-col gap-1.5 flex-1 min-w-0">
                  <span className="text-[#777] text-[10px] font-medium font-['Outfit']">To</span>
                  <div className="relative bg-white border border-[#e6e6e6] rounded-[6px] h-[40px] flex items-center justify-between pl-3 pr-2">
                    <select
                      value={yearTo}
                      onChange={e => setYearTo(e.target.value)}
                      className="bg-transparent outline-none appearance-none text-[#212121] text-[12px] font-medium font-['Outfit'] cursor-pointer w-full"
                    >
                      <option value="">Any</option>
                      {years.map(y => <option key={y} value={y}>{y}</option>)}
                    </select>
                    <ChevronDown size={14} className="text-[#374151] shrink-0 pointer-events-none absolute right-2" />
                  </div>
                </div>
              </div>
            </div>
            <div className="w-full h-px bg-neutral-200 shrink-0" />
          </>
        )}

        {/* Brand */}
        {brands.length > 0 && (
          <>
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-[#1a1917] text-[16px] font-semibold font-['Outfit'] tracking-[0.6px]">Brand</span>
                <ChevronDown size={16} className="text-[#374151] rotate-180" />
              </div>
              <div className="flex flex-col gap-2.5">
                {brands.slice(0, 5).map(brand => {
                  const isSelected = selectedFilters["Brand"]?.includes(brand);
                  return (
                    <label key={brand} className="flex items-center gap-3 cursor-pointer">
                      <div
                        onClick={() => toggle("Brand", brand)}
                        className={`w-[18px] h-[18px] rounded-[4px] border-[1.5px] flex items-center justify-center shrink-0 cursor-pointer transition-colors ${isSelected ? "bg-[#7C3AED] border-[#7C3AED]" : "bg-white border-[#d1d5db]"}`}
                      >
                        {isSelected && (
                          <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        )}
                      </div>
                      <span className="text-[#1a1917] text-[14px] font-medium font-['Outfit'] leading-5">{brand}</span>
                    </label>
                  );
                })}
              </div>
              {brands.length > 5 && (
                <div className="bg-[rgba(238,229,229,0.36)] flex items-center justify-between px-4 py-1 rounded-b-[10px]">
                  <span className="text-[#2563eb] text-[14px] font-semibold font-['Outfit'] leading-5">View More</span>
                  <ChevronDown size={16} className="text-[#2563eb]" />
                </div>
              )}
            </div>
            <div className="w-full h-px bg-neutral-200 shrink-0" />
          </>
        )}

        {/* Vehicle Type */}
        {vehicleTypes.length > 0 && (
          <>
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-[#1a1917] text-[16px] font-semibold font-['Outfit'] tracking-[0.6px]">Vehicle Type</span>
                <ChevronDown size={16} className="text-[#374151] rotate-180" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                {vehicleTypes.map(type => {
                  const isSelected = selectedFilters["Vehicle Body Type"]?.includes(type);
                  const img = BIKE_TYPE_IMG[type];
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => toggle("Vehicle Body Type", type)}
                      className={`relative flex flex-col items-center justify-end pb-2 rounded-[12px] border overflow-hidden transition-all h-[106px] ${isSelected ? "border-[#e8273c] bg-[#fff5f6]" : "border-[#e0e0e0] bg-white hover:border-[#9ca3af]"}`}
                    >
                      {isSelected && (
                        <div className="absolute top-1 right-1 w-[12px] h-[12px] bg-[#e8273c] rounded-full flex items-center justify-center">
                          <svg width="7" height="6" viewBox="0 0 10 8" fill="none">
                            <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                          </svg>
                        </div>
                      )}
                      <div className="flex-1 flex items-center justify-center w-full px-2 pt-2">
                        {img && (
                          <img
                            src={img}
                            alt={type}
                            className="w-full h-[54px] object-contain"
                            onError={e => { e.target.style.display = "none"; }}
                          />
                        )}
                      </div>
                      <span className={`text-[11px] font-semibold leading-none text-center px-1 mt-1 font-['Outfit'] ${isSelected ? "text-[#e8273c]" : "text-[#1a1a1a]"}`}>
                        {type}
                      </span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="w-full h-px bg-neutral-200 shrink-0" />
          </>
        )}

        {/* Seating Capacity */}
        {seatingOptions.length > 0 && (
          <>
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-[#1a1917] text-[16px] font-semibold font-['Outfit'] tracking-[0.6px]">Seating Capacity</span>
                <ChevronDown size={16} className="text-[#374151] rotate-180" />
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                {seatingOptions.map(seat => {
                  const isSelected = selectedFilters["Seater"]?.includes(String(seat));
                  return (
                    <button
                      key={seat}
                      type="button"
                      onClick={() => toggle("Seater", String(seat))}
                      className={`flex-1 h-[32px] rounded-[20px] border text-[13px] font-semibold font-['Outfit'] transition-all text-center ${isSelected ? "bg-[rgba(184,177,251,0.32)] border-[#babdee] text-[#7f1d1d] font-bold" : "bg-white border-[#e2e8f0] text-[#475569]"}`}
                    >
                      {seat}
                    </button>
                  );
                })}
                <span className="text-[#475569] text-[13px] font-semibold font-['Outfit']">Seats</span>
              </div>
            </div>
            <div className="w-full h-px bg-neutral-200 shrink-0" />
          </>
        )}

        {/* Transmission Type */}
        {transmissionTypes.length > 0 && (
          <>
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-[#1a1917] text-[16px] font-semibold font-['Outfit'] tracking-[0.6px]">Transmission Type</span>
                <ChevronDown size={16} className="text-[#374151] rotate-180" />
              </div>
              <div className="flex gap-3 flex-wrap">
                {transmissionTypes.map(type => {
                  const isSelected = selectedFilters["Transmission Type"]?.includes(type);
                  return (
                    <button
                      key={type}
                      type="button"
                      onClick={() => toggle("Transmission Type", type)}
                      className={`flex-1 h-[34px] rounded-[8px] border text-[11px] font-semibold font-['Outfit'] transition-all ${isSelected ? "bg-[rgba(184,177,251,0.32)] border-[#babdee] text-black" : "bg-white border-[#e0e0e0] text-[#665e5e]"}`}
                    >
                      {type}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="w-full h-px bg-neutral-200 shrink-0" />
          </>
        )}

        {/* Fuel Type */}
        {fuelTypes.length > 0 && (
          <>
            <div className="flex flex-col gap-3">
              <div className="flex items-center justify-between">
                <span className="text-[#1a1917] text-[16px] font-semibold font-['Outfit'] tracking-[0.6px]">Fuel Type</span>
                <ChevronDown size={16} className="text-[#374151] rotate-180" />
              </div>
              <div className="flex gap-3 flex-wrap">
                {fuelTypes.map(fuel => {
                  const isSelected = selectedFilters["Fuel Type"]?.includes(fuel);
                  return (
                    <button
                      key={fuel}
                      type="button"
                      onClick={() => toggle("Fuel Type", fuel)}
                      className={`flex-1 h-[34px] rounded-[8px] border text-[11px] font-semibold font-['Outfit'] transition-all ${isSelected ? "bg-[rgba(184,177,251,0.32)] border-[#babdee] text-black" : "bg-white border-[#e0e0e0] text-[#665e5e]"}`}
                    >
                      {fuel}
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="w-full h-px bg-neutral-200 shrink-0" />
          </>
        )}

        <div className="h-4 w-full shrink-0" />
      </div>
    </div>
  );
};

export default BikeFilterSidebar;
